/**
 * SelfServiceBookingDialog.tsx
 * Customer self-service dialog for booking updates
 * Shows FULL booking details (participants, addons, vouchers, price breakdown)
 * Allows customers to increase participants, add-ons, and apply vouchers
 *
 * Design: Uses corporate Frankenfloss palette (#910F0F), custom CSS (no MUI Dialog)
 *
 * CRITICAL RULES:
 * - Participants: only INCREASES of adults, until 24h before trip
 * - Food/Extras (T-095, v1.8.55, utils/gerichteverteilung): Wahlgerichte frei umverteilbar (Summe höchstens leistungsberechtigte
 *   Teilnehmer mal Multiplikator), kostenpflichtige Zusatzleistungen bis zur Schlussrechnung reduzierbar, Inklusivleistungen und
 *   Unterkünfte nur erhöhen; Frist aus der Einstellung (Standard 7 Tage vor der Fahrt)
 * - Automatic additional invoice for price increases, reductions are settled with the Schlussrechnung
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Calendar, Users, Euro, Tag, AlertTriangle, Info, CheckCircle, Plus, Minus, Coffee, Gift, Hotel } from 'lucide-react';
import BookingSystemAPI, {
  SelfServiceAvailability,
  SelfServiceCurrentAddOn,
  SelfServiceAvailableAddOn,
  SelfServiceUpdateRequest,
  SelfServiceValidationResult,
  SelfServiceResult
} from '../../services/BookingSystemAPI';
import { voucherService, ValidateVoucherResponse } from '../../services/voucherService';
import { artVon, darfReduzieren, maxWahlgerichte, bezahlteMenge, summenText, darfErhoehen, naechsteMenge } from '../../utils/gerichteverteilung';
import './SelfServiceBookingDialog.css';

// Types for booking data
interface BookingParticipants {
  adultCount: number;
  childCount: number;
  infantCount: number;
  freeParticipantWithSeatCount: number;
  freeParticipantNoSeatCount: number;
}

interface BookingAddOn {
  id: number;
  name: string;
  quantity: number;
  unitPrice: number;
}

interface BookingData {
  id: number;
  bookingNumber: string;
  tripDate: string;
  tripTime: string;
  tourTypeName: string;
  participantCount: number;
  participants: BookingParticipants;
  addOns: BookingAddOn[];
  totalPrice: number;
  basePrice: number;
  voucherCode?: string;
  voucherDiscount?: number;
}

interface SelfServiceBookingDialogProps {
  booking: BookingData;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: SelfServiceResult) => void;
}

const SelfServiceBookingDialog: React.FC<SelfServiceBookingDialogProps> = ({
  booking,
  isOpen,
  onClose,
  onSuccess
}) => {
  const api = new BookingSystemAPI();

  // Legacy fallback: old bookings have adult_count=0 but participant_count>0
  // Treat all participants as adults in that case
  const legacyCategorizedTotal = booking.participants.adultCount + booking.participants.childCount
    + booking.participants.infantCount + booking.participants.freeParticipantWithSeatCount
    + booking.participants.freeParticipantNoSeatCount;
  const originalAdultCount = (legacyCategorizedTotal === 0 && booking.participantCount > 0)
    ? booking.participantCount : booking.participants.adultCount;

  // State
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState<SelfServiceAvailability | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Form state - participant changes
  const [newAdultCount, setNewAdultCount] = useState(originalAdultCount);
  const [newChildCount, setNewChildCount] = useState(booking.participants.childCount);
  const [newInfantCount, setNewInfantCount] = useState(booking.participants.infantCount);
  const [newFreeWithSeat, setNewFreeWithSeat] = useState(booking.participants.freeParticipantWithSeatCount);
  const [newFreeNoSeat, setNewFreeNoSeat] = useState(booking.participants.freeParticipantNoSeatCount);

  // Form state - addon changes (addOnServiceId → new quantity)
  const [addonQuantities, setAddonQuantities] = useState<Record<number, number>>({});
  // Form state - new addons to add (addOnServiceId → quantity)
  const [newAddons, setNewAddons] = useState<Record<number, number>>({});

  // Form state - voucher
  const [voucherInput, setVoucherInput] = useState('');
  const [voucherValidating, setVoucherValidating] = useState(false);
  const [appliedVoucher, setAppliedVoucher] = useState<{
    code: string;
    remainingAmount: number;
    amountToApply: number;
    validUntil?: string;
  } | null>(null);
  const [voucherError, setVoucherError] = useState<string | null>(null);

  // Validation/Preview state
  const [validationResult, setValidationResult] = useState<SelfServiceValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successResult, setSuccessResult] = useState<SelfServiceResult | null>(null);

  // Load availability on open
  useEffect(() => {
    if (isOpen) {
      loadAvailability();
      setNewAdultCount(originalAdultCount);
      setNewChildCount(booking.participants.childCount);
      setNewInfantCount(booking.participants.infantCount);
      setNewFreeWithSeat(booking.participants.freeParticipantWithSeatCount);
      setNewFreeNoSeat(booking.participants.freeParticipantNoSeatCount);
      setAddonQuantities({});
      setNewAddons({});
      setVoucherInput('');
      setAppliedVoucher(null);
      setVoucherError(null);
      setShowPreview(false);
      setError(null);
      setValidationResult(null);
      setSuccessResult(null);
    }
  }, [isOpen, booking]);

  const loadAvailability = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getSelfServiceAvailability(booking.id);
      setAvailability(result);
      // Initialize addon quantities from server data
      if (result.currentAddOns) {
        const quantities: Record<number, number> = {};
        result.currentAddOns.forEach(a => {
          quantities[a.id] = a.quantity; // Review v1.8.55: je Position (id), nicht je Zusatzoption
        });
        setAddonQuantities(quantities);
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Verfügbarkeit');
    } finally {
      setLoading(false);
    }
  };

  // Get current addons from availability (server data)
  const currentAddOns: SelfServiceCurrentAddOn[] = useMemo(
    () => availability?.currentAddOns || [],
    [availability?.currentAddOns]
  );

  // Get available addons that are NOT already on the booking
  const availableNewAddOns: SelfServiceAvailableAddOn[] = useMemo(
    () => (availability?.availableAddOns || [])
      .filter(a => !currentAddOns.some(ca => ca.addOnServiceId === a.addOnServiceId)),
    [availability?.availableAddOns, currentAddOns]
  );

  // === AUTO-ADJUSTMENT: Calculate minimum addon quantities based on participant count ===
  // Use originalAdultCount (legacy fallback) instead of booking.participants.adultCount
  const originalTotalParticipants = originalAdultCount + booking.participants.childCount +
    booking.participants.infantCount + booking.participants.freeParticipantWithSeatCount +
    booking.participants.freeParticipantNoSeatCount;
  const newTotalParticipants = newAdultCount + newChildCount + newInfantCount + newFreeWithSeat + newFreeNoSeat;
  const participantDelta = newTotalParticipants - originalTotalParticipants;

  // T-095 (v1.8.55): leistungsberechtigte Teilnehmer (ohne Kinder 0-2) vor und nach der Änderung; der Gast meldet nur Erwachsene nach
  const berechtigteAlt = availability?.mahlzeitBerechtigte
    ?? Math.max(0, originalTotalParticipants - booking.participants.infantCount);
  const berechtigteNeu = berechtigteAlt + Math.max(0, newAdultCount - originalAdultCount);
  const zusatzReduzierbar = availability?.zusatzleistungenReduzierbar === true;
  // T-095: Höchstzahl der Wahlgerichte (Berechtigte nach der Änderung mal Multiplikator des ersten Wahlgerichts)
  const wahlMultiplikator = availability?.wahlgerichteMultiplikator
    ?? currentAddOns.find(a => artVon(a) === 'Wahlgericht')?.quantityMultiplier ?? 1;
  const maxWahl = maxWahlgerichte(berechtigteNeu, wahlMultiplikator);
  const summeWahlBisher = currentAddOns.filter(a => artVon(a) === 'Wahlgericht').reduce((s, a) => s + a.quantity, 0);

  // Review v1.8.55: mehrere aktive Positionen derselben Zusatzoption (z. B. in der Verwaltung zweimal hinzugefügt) ändert der Gast
  // nur über die erste Position, weitere werden nur angezeigt (der Server ordnet Änderungen der ersten Position zu)
  const istErsteZeile = useCallback((addon: SelfServiceCurrentAddOn): boolean =>
    currentAddOns.find(a => a.addOnServiceId === addon.addOnServiceId)?.id === addon.id, [currentAddOns]);

  // T-062 (18.09.2026, Kundin-Befund 17.09.): Wahlgerichte ("Auswahl pro Teilnehmer", isOptionalChoice)
  // teilen sich EINE Menge je Teilnehmer. Der Teilnehmer-Zuwachs darf deshalb nur EINMAL auf die
  // Gruppe wirken (bisher bekam jedes Gericht das volle Delta: 32 + 5, 2 + 5, 2 + 5 = 51 fuer 41 Personen).
  // Die zusaetzlichen Gerichte werden dem ersten Wahlgericht zugeschlagen (Hinweis im Dialog).
  // Der Multiplikator "Menge pro Teilnehmer" gilt laut Tour-Konfiguration nur fuer Wahlgerichte, nicht fuer andere
  // Inklusivleistungen (Getraenkeflat mit Multiplikator 200 ergab 36 + 5 x 200 = 1036).
  // T-095 (v1.8.55): erste Wahlgericht-Position der Buchung (Art vom Server); die Vorbelegung ist nur ein Vorschlag, der Gast
  // verteilt frei. Review: Vorbelegung höchstens bis zur neuen Höchstzahl (Altbuchungen mit mehr Gerichten als Teilnehmern).
  const firstOptionalChoiceRowId = useMemo(() => {
    const first = currentAddOns.find(a => artVon(a) === 'Wahlgericht');
    return first ? first.id : null;
  }, [currentAddOns]);
  const wahlZuwachs = participantDelta > 0
    ? Math.min(participantDelta * Math.max(1, wahlMultiplikator), Math.max(0, maxWahl - summeWahlBisher))
    : 0;

  const getParticipantDeltaFor = useCallback((addon: SelfServiceCurrentAddOn): number => {
    if (participantDelta <= 0 || !istErsteZeile(addon)) return 0;
    if (artVon(addon) === 'Wahlgericht') {
      // Zuwachs nur einmal, bei der ersten Wahlgericht-Position, mit Multiplikator (z.B. 2 Gerichte je Person)
      return addon.id === firstOptionalChoiceRowId ? wahlZuwachs : 0;
    }
    // Sonstige Inklusivleistungen (z.B. Getraenkeflat): eine Einheit je zusaetzlichem Teilnehmer
    return participantDelta;
  }, [participantDelta, firstOptionalChoiceRowId, wahlZuwachs, istErsteZeile]);

  // Get the auto-calculated minimum quantity for an addon based on participant count
  const getAutoMinQuantity = useCallback((addon: SelfServiceCurrentAddOn): number => {
    // Only auto-adjust addons that have inclusive quantities and scale with participants
    if (addon.quantityFree <= 0 && !addon.isIncludedInTourPrice) return addon.quantity;

    // PerGroup, PerNight, PerHour, PerDay don't scale with participants
    const pt = addon.pricingType;
    if (pt === 'PerGroup' || pt === 'PerNight' || pt === 'PerHour' || pt === 'PerDay') {
      return addon.quantity;
    }

    if (addon.isAccommodation && addon.bedsPerUnit && addon.bedsPerUnit > 0) {
      if (!istErsteZeile(addon)) return addon.quantity;
      return Math.max(addon.quantity, Math.ceil(newTotalParticipants / addon.bedsPerUnit));
    }

    // PerPerson/Fixed: scale with participant delta (T-062: Wahlgerichte nur einmal, kein
    // Multiplikator fuer sonstige Inklusivleistungen)
    return Math.max(addon.quantity, addon.quantity + getParticipantDeltaFor(addon));
  }, [newTotalParticipants, getParticipantDeltaFor, istErsteZeile]);

  // T-095 (v1.8.55, Review): Gast darf senken (Server-Merkmal, sonst nach Art) und ändern (Zusatz außerhalb des Angebots nicht)
  const istReduzierbar = useCallback((addon: SelfServiceCurrentAddOn): boolean =>
    istErsteZeile(addon) && (addon.reduzierbar ?? darfReduzieren(artVon(addon), zusatzReduzierbar)), [istErsteZeile, zusatzReduzierbar]);
  const istNurAnzeige = useCallback((addon: SelfServiceCurrentAddOn): boolean =>
    !istErsteZeile(addon) || (artVon(addon) === 'Zusatz' && addon.imAngebot === false), [istErsteZeile]);

  // Get effective addon quantity (max of user-set qty and auto-min)
  // T-095 (v1.8.55): Wahlgerichte frei (Vorbelegung des Zuwachses bei der ersten Wahlgericht-Position, bis 0 reduzierbar),
  // reduzierbare Zusatzleistungen bis 0, Inklusivleistungen und Unterkünfte mindestens auto-min; Werte je Position (id)
  const getEffectiveAddonQuantity = useCallback((addon: SelfServiceCurrentAddOn): number => {
    if (!istErsteZeile(addon)) return addon.quantity;
    const userQty = addonQuantities[addon.id] ?? addon.quantity;
    const art = artVon(addon);
    if (art === 'Wahlgericht') return Math.max(0, userQty + getParticipantDeltaFor(addon));
    if (art === 'Zusatz' && istReduzierbar(addon)) return Math.max(0, userQty);
    const autoMin = getAutoMinQuantity(addon);
    return Math.max(userQty, autoMin);
  }, [addonQuantities, getAutoMinQuantity, getParticipantDeltaFor, istErsteZeile, istReduzierbar]);

  // T-095 (v1.8.55): kleinste erlaubte Menge (Minus-Knopf)
  const getMinAddonQuantity = useCallback((addon: SelfServiceCurrentAddOn): number => {
    return istReduzierbar(addon) ? 0 : getAutoMinQuantity(addon);
  }, [getAutoMinQuantity, istReduzierbar]);

  // T-095 (v1.8.55): Menge setzen (bei Wahlgerichten ohne die Vorbelegung des Zuwachses gespeichert), je Position (id)
  const setEffectiveAddonQuantity = (addon: SelfServiceCurrentAddOn, value: number) => {
    if (!istErsteZeile(addon)) return;
    const offset = artVon(addon) === 'Wahlgericht' ? getParticipantDeltaFor(addon) : 0;
    setAddonQuantities(prev => ({ ...prev, [addon.id]: Math.max(0, value) - offset }));
  };

  // Get the adjusted quantityFree for an addon, accounting for participant changes
  // CRITICAL: When free participants (Kleinkinder, Freiplätze) are added,
  // the inclusive quantity must also increase - they get Inklusivleistungen too!
  const getAdjustedQuantityFree = useCallback((addon: SelfServiceCurrentAddOn): number => {
    if (addon.quantityFree <= 0) return 0;

    // Non-per-person addons don't scale with participants
    const pt = addon.pricingType;
    if (pt === 'PerGroup' || pt === 'PerNight' || pt === 'PerHour' || pt === 'PerDay') {
      return addon.quantityFree;
    }

    // T-062: gleiche Verteilung wie bei der Menge (Wahlgerichte nur einmal, sonst 1 je Teilnehmer)
    return addon.quantityFree + getParticipantDeltaFor(addon);
  }, [getParticipantDeltaFor]);

  // T-095 (v1.8.55): bezahlte Menge wie die Rechnung (Wahlgerichte und Inklusivleistungen frei bis zur Zahl der Berechtigten,
  // InvoiceService.CalculateInclusiveQuantities), sonst mit der angepassten freien Menge
  const getAddonPaidQty = useCallback((addon: SelfServiceCurrentAddOn, effectiveQty: number): number => {
    const art = artVon(addon);
    if (art === 'Wahlgericht' || art === 'Inklusiv') return bezahlteMenge(art, addon.pricingType, effectiveQty, berechtigteNeu, 0);
    return Math.max(0, effectiveQty - getAdjustedQuantityFree(addon));
  }, [getAdjustedQuantityFree, berechtigteNeu]);

  // Get the paid cost for an addon (respecting free/included quantities)
  const getAddonPaidCost = useCallback((addon: SelfServiceCurrentAddOn, effectiveQty: number): number => {
    return getAddonPaidQty(addon, effectiveQty) * addon.unitPrice;
  }, [getAddonPaidQty]);

  // Check if participant fields changed
  const hasParticipantChanges = useCallback((): boolean => {
    return (
      newAdultCount !== originalAdultCount ||
      newChildCount !== booking.participants.childCount ||
      newInfantCount !== booking.participants.infantCount ||
      newFreeWithSeat !== booking.participants.freeParticipantWithSeatCount ||
      newFreeNoSeat !== booking.participants.freeParticipantNoSeatCount
    );
  }, [newAdultCount, newChildCount, newInfantCount, newFreeWithSeat, newFreeNoSeat, booking, originalAdultCount]);

  // Check if addon quantities changed (including auto-adjustments from participant changes)
  const hasAddonChanges = useCallback((): boolean => {
    for (const addon of currentAddOns) {
      const effectiveQty = getEffectiveAddonQuantity(addon);
      if (effectiveQty !== addon.quantity) return true;
    }
    for (const qty of Object.values(newAddons)) {
      if (qty > 0) return true;
    }
    return false;
  }, [currentAddOns, getEffectiveAddonQuantity, newAddons]);

  // Check if there are any changes (participants, addons, or voucher)
  const hasChanges = useCallback((): boolean => {
    return hasParticipantChanges() || hasAddonChanges() || appliedVoucher !== null;
  }, [hasParticipantChanges, hasAddonChanges, appliedVoucher]);

  // Build update request from current form state
  const buildUpdateRequest = useCallback((): SelfServiceUpdateRequest => {
    const request: SelfServiceUpdateRequest = {};
    if (newAdultCount !== originalAdultCount) request.adultCount = newAdultCount;
    if (newChildCount !== booking.participants.childCount) request.childCount = newChildCount;
    if (newInfantCount !== booking.participants.infantCount) request.infantCount = newInfantCount;
    if (newFreeWithSeat !== booking.participants.freeParticipantWithSeatCount) request.freeParticipantWithSeatCount = newFreeWithSeat;
    if (newFreeNoSeat !== booking.participants.freeParticipantNoSeatCount) request.freeParticipantNoSeatCount = newFreeNoSeat;

    // Build addon changes (using effective quantities with auto-adjustments)
    const addOnChanges: { addOnId: number; newQuantity: number }[] = [];

    // T-095 (v1.8.55): jede Änderung (auch Reduktion), der Server prüft die Regeln
    for (const addon of currentAddOns) {
      const effectiveQty = getEffectiveAddonQuantity(addon);
      if (effectiveQty !== addon.quantity) {
        addOnChanges.push({ addOnId: addon.addOnServiceId, newQuantity: effectiveQty });
      }
    }

    // New addons
    for (const [idStr, qty] of Object.entries(newAddons)) {
      if (qty > 0) {
        addOnChanges.push({ addOnId: parseInt(idStr), newQuantity: qty });
      }
    }

    if (addOnChanges.length > 0) {
      request.addOnChanges = addOnChanges;
    }

    // Voucher
    if (appliedVoucher) {
      request.voucherCode = appliedVoucher.code;
    }

    return request;
  }, [newAdultCount, newChildCount, newInfantCount, newFreeWithSeat, newFreeNoSeat,
      newAddons, appliedVoucher, booking, currentAddOns, getEffectiveAddonQuantity]);

  // Validate changes and show preview
  const validateAndPreview = async () => {
    if (!hasChanges()) {
      setError('Bitte nehmen Sie mindestens eine Änderung vor.');
      return;
    }
    setValidating(true);
    setError(null);
    try {
      const request = buildUpdateRequest();
      const result = await api.validateSelfServiceUpdate(booking.id, request);
      setValidationResult(result);
      if (result.isValid) {
        setShowPreview(true);
      } else {
        setError(result.errors.join('\n'));
      }
    } catch (err: any) {
      setError(err.message || 'Fehler bei der Validierung');
    } finally {
      setValidating(false);
    }
  };

  // Process the update
  const processUpdate = async () => {
    setProcessing(true);
    setError(null);
    try {
      const request = buildUpdateRequest();
      const result = await api.processSelfServiceUpdate(booking.id, request);
      if (result.success) {
        setSuccessResult(result); // Show success view first, onSuccess called on close
      } else {
        setError(result.errorMessage || 'Fehler bei der Verarbeitung');
      }
    } catch (err: any) {
      setError(err.message || 'Fehler bei der Verarbeitung');
    } finally {
      setProcessing(false);
    }
  };

  // Voucher validation
  const handleVoucherValidate = async () => {
    if (!voucherInput.trim()) {
      setVoucherError('Bitte geben Sie einen Gutschein-Code ein');
      return;
    }
    setVoucherValidating(true);
    setVoucherError(null);
    try {
      const result: ValidateVoucherResponse = await voucherService.validateVoucher(voucherInput.trim());
      if (!result.isValid) {
        setVoucherError(result.message);
        return;
      }
      const amountToApply = result.remainingAmount || 0;
      if (amountToApply <= 0) {
        setVoucherError('Der Gutschein hat keinen anwendbaren Wert');
        return;
      }
      setAppliedVoucher({
        code: result.code || voucherInput.trim().toUpperCase(),
        remainingAmount: result.remainingAmount || 0,
        amountToApply,
        validUntil: result.validUntil,
      });
    } catch (err: any) {
      setVoucherError(err.message || 'Fehler bei der Gutschein-Validierung');
    } finally {
      setVoucherValidating(false);
    }
  };

  const handleVoucherRemove = () => {
    setAppliedVoucher(null);
    setVoucherInput('');
    setVoucherError(null);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('de-DE', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
      });
    } catch { return dateString; }
  };

  const formatTime = (timeStr: string) => {
    const parts = timeStr.toString().split(':');
    return parts.length >= 2 ? `${parts[0]}:${parts[1]} Uhr` : timeStr;
  };

  // Calculate live price estimates using DIFFERENCE approach
  // Instead of rebuilding the total (which misses components), we calculate
  // the difference caused by changes and add it to booking.totalPrice
  const basePrice = booking.basePrice;

  // Display values for individual line items
  // Kinder-Nachmeldung ist per Self-Service gesperrt, daher fliessen nur Erwachsene in die Differenz ein
  const newAdultPrice = newAdultCount * basePrice;

  // Participant price difference (Self-Service: nur Erwachsene aenderbar)
  const oldPayingPrice = originalAdultCount * basePrice;
  const newPayingPrice = newAdultPrice;
  const participantPriceDiff = newPayingPrice - oldPayingPrice;

  // Addon price difference: new paid total minus original paid total
  // Original paid costs (what was already charged before any changes)
  let originalAddonsPaid = 0;
  for (const addon of currentAddOns) {
    // T-095 (v1.8.55): wie die Rechnung (Wahlgerichte und Inklusivleistungen frei bis zur Zahl der Berechtigten vor der Änderung)
    const art = artVon(addon);
    const originalPaidQty = (art === 'Wahlgericht' || art === 'Inklusiv')
      ? bezahlteMenge(art, addon.pricingType, addon.quantity, berechtigteAlt, 0)
      : Math.max(0, addon.quantity - addon.quantityFree);
    originalAddonsPaid += originalPaidQty * addon.unitPrice;
  }

  // New paid costs (after changes, with adjusted free quantities)
  let newAddonsPaid = 0;
  for (const addon of currentAddOns) {
    const effectiveQty = getEffectiveAddonQuantity(addon);
    newAddonsPaid += getAddonPaidCost(addon, effectiveQty);
  }
  // Plus any newly added addons
  for (const [idStr, qty] of Object.entries(newAddons)) {
    if (qty > 0) {
      const avail = availableNewAddOns.find(a => a.addOnServiceId === parseInt(idStr));
      if (avail) newAddonsPaid += avail.unitPrice * bezahlteMenge(artVon(avail), avail.pricingType, qty, berechtigteNeu, 0);
    }
  }
  const addonPriceDiff = newAddonsPaid - originalAddonsPaid;

  const existingVoucherDiscount = availability?.voucherDiscount ?? booking.voucherDiscount ?? 0;
  const newVoucherDiscount = appliedVoucher ? appliedVoucher.amountToApply : 0;

  // New total = old total + differences - new voucher
  const estimatedNewTotal = booking.totalPrice + participantPriceDiff + addonPriceDiff - newVoucherDiscount;
  const estimatedDifference = estimatedNewTotal - booking.totalPrice;

  const totalParticipants = newTotalParticipants;

  // T-095 (v1.8.55): Gerichte (Auswahl pro Teilnehmer), gebuchte und noch nicht gebuchte, mit Summe gegen die Höchstzahl
  const wahlZeilen = [
    ...currentAddOns.filter(a => artVon(a) === 'Wahlgericht').map(a => ({
      key: `c${a.id}`,
      name: a.name,
      menge: getEffectiveAddonQuantity(a),
      bisher: a.quantity,
      unitPrice: a.unitPrice,
      pricingType: a.pricingType,
      nurAnzeige: !istErsteZeile(a),
      setzen: (wert: number) => setEffectiveAddonQuantity(a, wert)
    })),
    ...availableNewAddOns.filter(a => artVon(a) === 'Wahlgericht').map(a => ({
      key: `n${a.addOnServiceId}`,
      name: a.name,
      menge: newAddons[a.addOnServiceId] ?? 0,
      bisher: 0,
      unitPrice: a.unitPrice,
      pricingType: a.pricingType,
      nurAnzeige: false,
      setzen: (wert: number) => setNewAddons(prev => ({ ...prev, [a.addOnServiceId]: Math.max(0, wert) }))
    }))
  ];
  const summeWahl = wahlZeilen.reduce((s, z) => s + z.menge, 0);
  const sonstigeAddOns = currentAddOns.filter(a => artVon(a) !== 'Wahlgericht');
  const weitereAddOns = availableNewAddOns.filter(a => artVon(a) !== 'Wahlgericht');

  if (!isOpen) return null;

  // ===== SUCCESS VIEW =====
  if (successResult) {
    return (
      <div className="selfservice-overlay" onClick={() => { onSuccess(successResult!); onClose(); }}>
        <div className="selfservice-dialog" onClick={e => e.stopPropagation()}>
          <div className="selfservice-header">
            <div className="selfservice-header-content">
              <h3>Buchung aktualisiert</h3>
              <div className="selfservice-booking-nr">{booking.bookingNumber}</div>
            </div>
            <button className="selfservice-close" onClick={() => { onSuccess(successResult!); onClose(); }}><X /></button>
          </div>
          <div className="selfservice-body">
            <div className="selfservice-success">
              <CheckCircle className="selfservice-success-icon" />
              <h4>Änderung erfolgreich!</h4>
              <p>Ihre Buchung wurde aktualisiert.</p>
              {(successResult.voucherRedemptionAmount ?? 0) > 0 && (
                <div className="selfservice-success-voucher">
                  <Tag size={16} />
                  <span>Gutschein eingelöst: -{formatCurrency(successResult.voucherRedemptionAmount!)}</span>
                </div>
              )}
              {successResult.additionalAmount > 0 && (
                <div className="selfservice-success-card">
                  <div>Eine Nachtragsrechnung wurde erstellt (zahlbar innerhalb von 7 Tagen):</div>
                  <div className="amount">{formatCurrency(successResult.additionalAmount)}</div>
                  {successResult.additionalInvoiceNumber && (
                    <div className="invoice-nr">Rechnungsnr.: {successResult.additionalInvoiceNumber}</div>
                  )}
                </div>
              )}
              {successResult.hinweis && (
                <div className="selfservice-success-card">
                  <div>{successResult.hinweis}</div>
                </div>
              )}
              {successResult.additionalAmount <= 0 && (successResult.voucherRedemptionAmount ?? 0) > 0 && (
                <div className="selfservice-success-card">
                  <div>Der Gutschein deckt die Nachzahlung vollständig ab.</div>
                </div>
              )}
            </div>
          </div>
          <div className="selfservice-footer">
            <button className="selfservice-btn primary" onClick={() => { onSuccess(successResult!); onClose(); }} style={{ flex: 1 }}>
              Schließen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== PREVIEW/CONFIRMATION VIEW =====
  if (showPreview && validationResult?.preview) {
    const preview = validationResult.preview;
    return (
      <div className="selfservice-overlay" onClick={onClose}>
        <div className="selfservice-dialog" onClick={e => e.stopPropagation()}>
          <div className="selfservice-header">
            <div className="selfservice-header-content">
              <h3>Änderungen bestätigen</h3>
              <div className="selfservice-booking-nr">{booking.bookingNumber}</div>
            </div>
            <button className="selfservice-close" onClick={onClose}><X /></button>
          </div>
          <div className="selfservice-body">
            {/* Changes Summary */}
            {preview.changesSummary && (
              <div className="selfservice-section">
                <div className="selfservice-section-header">
                  <Info size={18} />
                  <h4>Ihre Änderungen</h4>
                </div>
                <div className="selfservice-section-body">
                  {preview.changesSummary.participantChanges.map((c, i) => (
                    <div key={i} className="selfservice-row">
                      <span className="selfservice-label">{c}</span>
                    </div>
                  ))}
                  {preview.changesSummary.addOnChanges?.map((c, i) => (
                    <div key={`a${i}`} className="selfservice-row">
                      <span className="selfservice-label">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Price breakdown of changes */}
            {preview.changesSummary?.priceChanges && preview.changesSummary.priceChanges.length > 0 && (
              <div className="selfservice-section">
                <div className="selfservice-section-header">
                  <Euro size={18} />
                  {/* T-095 (v1.8.55): bei Reduktionen keine Nachberechnung, sondern Preisänderung */}
                  <h4>{preview.changesSummary.priceChanges.some(p => p.lineTotal < 0) ? 'Preisänderung' : 'Nachberechnung'}</h4>
                </div>
                <div className="selfservice-section-body">
                  {preview.changesSummary.priceChanges.map((item, i) => (
                    <div key={i} className="selfservice-row">
                      <span className="selfservice-label">{item.description}</span>
                      <span className="selfservice-value">
                        {item.quantity}× {formatCurrency(item.unitPrice)} = {formatCurrency(item.lineTotal)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="selfservice-price-summary">
              <div className="selfservice-section-header">
                <Euro size={18} />
                <h4>Preisübersicht</h4>
              </div>
              <div className="selfservice-price-row">
                <span>Bisheriger Gesamtpreis</span>
                <span className="selfservice-price-amount">{formatCurrency(preview.originalTotal)}</span>
              </div>
              <div className="selfservice-price-row">
                <span>Neuer Gesamtpreis</span>
                <span className="selfservice-price-amount changed">{formatCurrency(preview.newTotal)}</span>
              </div>
              {(preview.voucherRedemptionAmount ?? 0) > 0 && (
                <div className="selfservice-price-row" style={{ color: '#16a34a' }}>
                  <span>Gutschein-Abzug</span>
                  <span style={{ fontWeight: 600 }}>-{formatCurrency(preview.voucherRedemptionAmount!)}</span>
                </div>
              )}
              <div className="selfservice-price-divider" />
              <div className="selfservice-price-row nachzahlung">
                <span>Nachzahlung</span>
                <span>{formatCurrency(preview.additionalAmount)}</span>
              </div>
            </div>

            {/* T-159 (v1.8.45): Hinweis zur Abrechnung, z. B. Staffelwechsel ohne automatische Nachtragsrechnung */}
            {preview.hinweis && (
              <div className="selfservice-deadline warning">
                <AlertTriangle />
                <span>{preview.hinweis}</span>
              </div>
            )}

            {/* Warnings */}
            {validationResult.warnings && validationResult.warnings.length > 0 && (
              <div className="selfservice-deadline warning">
                <AlertTriangle />
                <span>{validationResult.warnings.join(' ')}</span>
              </div>
            )}

            {error && (
              <div className="selfservice-error">
                <AlertTriangle />
                <span>{error}</span>
              </div>
            )}
          </div>
          <div className="selfservice-footer">
            <button className="selfservice-btn secondary" onClick={() => setShowPreview(false)}>
              Zurück
            </button>
            <button
              className="selfservice-btn success"
              onClick={processUpdate}
              disabled={processing}
            >
              {processing ? <><span className="selfservice-btn-spinner" /> Verarbeite...</> : <><CheckCircle size={16} /> Änderung bestätigen</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== MAIN BOOKING VIEW =====
  return (
    <div className="selfservice-overlay" onClick={onClose}>
      <div className="selfservice-dialog" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="selfservice-header">
          <div className="selfservice-header-content">
            <h3>Buchung anpassen</h3>
            <div className="selfservice-booking-nr">{booking.bookingNumber}</div>
          </div>
          <button className="selfservice-close" onClick={onClose}><X /></button>
        </div>

        <div className="selfservice-body">
          {/* Loading */}
          {loading && (
            <div className="selfservice-loading">
              <div className="selfservice-spinner" />
              <p>Buchung wird geladen...</p>
            </div>
          )}

          {/* Not Available */}
          {!loading && availability && !availability.isAvailable && (
            <div className="selfservice-unavailable">
              <AlertTriangle />
              <h4>Änderungen nicht mehr möglich</h4>
              <p>{availability.unavailableReason || 'Die Änderungsfrist ist abgelaufen.'}</p>
            </div>
          )}

          {/* Main Content */}
          {!loading && availability?.isAvailable && (
            <>
              {/* Deadline Info */}
              {availability.participantChangesAllowed && availability.participantDeadline && (
                <div className="selfservice-deadline info">
                  <Info />
                  {/* FIX 24.08.2026 (Kundin-Wunsch 23.08.): kein Anspruchs-Eindruck, Nachmeldungen nur bei Kapazitaet */}
                  <span>Teilnehmer-Nachmeldungen bei entsprechenden Kapazitäten theoretisch möglich bis: <strong>{formatDate(availability.participantDeadline)}</strong></span>
                </div>
              )}
              {!availability.participantChangesAllowed && (
                <div className="selfservice-deadline warning">
                  <AlertTriangle />
                  <span>Teilnehmer-Änderungen nicht mehr möglich (Frist abgelaufen)</span>
                </div>
              )}

              {/* === SECTION: Termindetails === */}
              <div className="selfservice-section">
                <div className="selfservice-section-header">
                  <Calendar size={18} />
                  <h4>Termindetails</h4>
                </div>
                <div className="selfservice-section-body">
                  <div className="selfservice-row">
                    <span className="selfservice-label">Tour</span>
                    <span className="selfservice-value">{booking.tourTypeName}</span>
                  </div>
                  <div className="selfservice-row">
                    <span className="selfservice-label">Datum</span>
                    <span className="selfservice-value">{formatDate(booking.tripDate)}</span>
                  </div>
                  <div className="selfservice-row">
                    <span className="selfservice-label">Uhrzeit</span>
                    <span className="selfservice-value">{formatTime(booking.tripTime)}</span>
                  </div>
                </div>
              </div>

              {/* === SECTION: Teilnehmer (editable) === */}
              <div className="selfservice-section">
                <div className="selfservice-section-header">
                  <Users size={18} />
                  <h4>Teilnehmer</h4>
                  {hasParticipantChanges() && <span className="selfservice-change-badge">geändert</span>}
                </div>
                <div className="selfservice-section-body">
                  {/* Erwachsene */}
                  <div className="selfservice-participant-row">
                    <div className="selfservice-participant-info">
                      {/* FIX 24.08.2026 (Kundin-Korrektur 23.08.): Erwachsene zaehlen ab 13 Jahren (Anlage v1.3 Abschnitt 2.1) */}
                      <div className="selfservice-participant-name">Erwachsene (ab 13 J.)</div>
                      <div className="selfservice-participant-detail">{formatCurrency(basePrice)} pro Person</div>
                    </div>
                    <div className="selfservice-counter">
                      <button
                        className="selfservice-counter-btn"
                        onClick={() => setNewAdultCount(Math.max(originalAdultCount, newAdultCount - 1))}
                        disabled={!availability.participantChangesAllowed || newAdultCount <= originalAdultCount}
                      ><Minus size={16} /></button>
                      <span className={`selfservice-counter-value${newAdultCount !== originalAdultCount ? ' changed' : ''}`}>
                        {newAdultCount}
                      </span>
                      <button
                        className="selfservice-counter-btn"
                        onClick={() => setNewAdultCount(newAdultCount + 1)}
                        disabled={!availability.participantChangesAllowed}
                      ><Plus size={16} /></button>
                    </div>
                  </div>

                  {/* Kinder und Freiplaetze: Nachmeldung NUR ueber Frankenfloss (Anlage v1.3 Abschnitt 2.3 Punkt 2) */}
                  {(booking.participants.childCount > 0 || booking.participants.infantCount > 0 ||
                    booking.participants.freeParticipantWithSeatCount > 0 || booking.participants.freeParticipantNoSeatCount > 0) && (
                    <div className="selfservice-participant-row">
                      <div className="selfservice-participant-info">
                        <div className="selfservice-participant-name">Kinder und Freiplätze</div>
                        <div className="selfservice-participant-detail">
                          {booking.participants.childCount + booking.participants.infantCount} Kinder,{' '}
                          {booking.participants.freeParticipantWithSeatCount + booking.participants.freeParticipantNoSeatCount} Freiplätze (gebucht)
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="selfservice-capacity">
                    <span>
                      Kinder und Freiplätze können nur durch Frankenfloß nachgemeldet werden.
                      Bitte kontaktieren Sie uns telefonisch oder per E-Mail.
                    </span>
                  </div>
                  {participantDelta > 0 && firstOptionalChoiceRowId !== null && (
                    <div className="selfservice-capacity">
                      <span>
                        {/* T-095 (v1.8.55): Umverteilung bis zur Frist im Kundenkonto */}
                        {availability.addOnChangesAllowed
                          ? 'Die Bordspeisen für zusätzliche Teilnehmer werden zunächst mit dem ersten Gericht vorbelegt. Unter "Verpflegung & Extras" können Sie die Gerichte frei verteilen.'
                          : 'Die Bordspeisen für zusätzliche Teilnehmer werden mit dem ersten Gericht vorbelegt. Eine andere Auswahl teilen Sie uns bitte telefonisch oder per E-Mail mit.'}
                      </span>
                    </div>
                  )}

                  <div className="selfservice-capacity">
                    <Users size={14} />
                    <span>Gesamt: {totalParticipants} Personen | Verfügbare Plätze: {availability.availableCapacity}</span>
                  </div>
                </div>
              </div>

              {/* === SECTION: Verpflegung & Extras (editable) === */}
              {(currentAddOns.length > 0 || wahlZeilen.length > 0) && (
                <div className="selfservice-section">
                  <div className="selfservice-section-header">
                    <Coffee size={18} />
                    <h4>Verpflegung & Extras</h4>
                    {hasAddonChanges() && <span className="selfservice-change-badge">geändert</span>}
                  </div>
                  <div className="selfservice-section-body">
                    {/* Addon deadline info */}
                    {availability.addOnChangesAllowed && availability.addOnDeadline && (
                      <div className="selfservice-addon-deadline-info">
                        <Info size={14} />
                        <span>Änderungen möglich bis: <strong>{formatDate(availability.addOnDeadline)}</strong></span>
                      </div>
                    )}
                    {!availability.addOnChangesAllowed && (
                      <div className="selfservice-addon-deadline-warning">
                        <AlertTriangle size={14} />
                        <span>Extras-Änderungen nicht mehr möglich (Frist abgelaufen)</span>
                      </div>
                    )}

                    {/* T-095 (v1.8.55): Gerichte (Auswahl pro Teilnehmer) frei verteilen, höchstens 1 Gericht je berechtigtem Teilnehmer */}
                    {wahlZeilen.length > 0 && (
                      <div className="selfservice-wahlgerichte">
                        <div className="selfservice-participant-detail" style={{ padding: '2px 0 6px' }}>
                          Gerichte (Auswahl pro Teilnehmer){availability.addOnChangesAllowed ? ': bis zur Frist frei verteilbar' : ''}
                        </div>
                        {wahlZeilen.map((zeile) => {
                          const bezahlt = bezahlteMenge('Wahlgericht', zeile.pricingType, zeile.menge, berechtigteNeu, 0);
                          const geaendert = zeile.menge !== zeile.bisher;
                          return (
                            <div key={zeile.key} className="selfservice-participant-row">
                              <div className="selfservice-participant-info">
                                <div className="selfservice-participant-name">
                                  {zeile.name}
                                  <span className="selfservice-addon-free-badge">
                                    {bezahlt > 0 ? `${zeile.menge - bezahlt} inkl.` : 'Inklusive'}
                                  </span>
                                </div>
                                <div className="selfservice-participant-detail">
                                  {bezahlt > 0 ? `${formatCurrency(zeile.unitPrice)} je weiteres Gericht` : 'Im Tourpreis enthalten'}
                                </div>
                              </div>
                              <div className="selfservice-addon-total-col">
                                <span className={`selfservice-addon-line-total${geaendert ? ' changed' : ''}`}>
                                  {bezahlt > 0 ? formatCurrency(bezahlt * zeile.unitPrice) : 'Inklusive'}
                                </span>
                              </div>
                              <div className="selfservice-counter">
                                <button
                                  className="selfservice-counter-btn"
                                  aria-label={`${zeile.name} verringern`}
                                  onClick={() => zeile.setzen(zeile.menge - 1)}
                                  disabled={!availability.addOnChangesAllowed || zeile.nurAnzeige || zeile.menge <= 0}
                                ><Minus size={16} /></button>
                                <span className={`selfservice-counter-value${geaendert ? ' changed' : ''}`}>
                                  {zeile.menge}
                                </span>
                                <button
                                  className="selfservice-counter-btn"
                                  aria-label={`${zeile.name} erhöhen`}
                                  onClick={() => zeile.setzen(zeile.menge + 1)}
                                  disabled={!availability.addOnChangesAllowed || zeile.nurAnzeige || !darfErhoehen('Wahlgericht', summeWahl, maxWahl)}
                                ><Plus size={16} /></button>
                              </div>
                            </div>
                          );
                        })}
                        <div
                          className="selfservice-wahlgerichte-summe"
                          style={{
                            padding: '6px 12px',
                            fontSize: '0.85rem',
                            borderRadius: '6px',
                            margin: '4px 0 10px 0',
                            background: summeWahl > maxWahl ? '#fee2e2' : (summeWahl === maxWahl ? '#dcfce7' : '#fef9c3'),
                            color: summeWahl > maxWahl ? '#991b1b' : (summeWahl === maxWahl ? '#166534' : '#854d0e')
                          }}
                        >
                          {summenText(summeWahl, maxWahl)}
                        </div>
                      </div>
                    )}

                    {sonstigeAddOns.map((addon) => {
                      const effectiveQty = getEffectiveAddonQuantity(addon);
                      const minQty = getMinAddonQuantity(addon);
                      const isChanged = effectiveQty !== addon.quantity;
                      const paidCost = getAddonPaidCost(addon, effectiveQty);
                      // FIX 24.08.2026 (Kundin-Korrektur 23.08.): Vollstaendig inkludierte
                      // Leistungen (z.B. Getraenkeflat) sind KEINE waehlbaren Extras.
                      // Keine Preisangabe pro Einheit, kein Zaehler, die Menge folgt
                      // automatisch der Teilnehmerzahl.
                      const isFullyInclusive = addon.isIncludedInTourPrice ||
                        (addon.quantityFree > 0 && paidCost === 0 && effectiveQty <= getAdjustedQuantityFree(addon));
                      // T-095 (v1.8.55, Review): weitere Positionen derselben Zusatzoption und Zusatzleistungen außerhalb des
                      // Online-Angebots (z. B. Pauschalen der Verwaltung) nur anzeigen; Mindest- und Höchstmenge beachten
                      const nurAnzeige = istNurAnzeige(addon);
                      const grenzen = {
                        min: minQty,
                        mindestMenge: artVon(addon) === 'Zusatz' ? addon.mindestMenge : 0,
                        hoechstMenge: addon.hoechstMenge
                      };
                      return (
                        <div key={addon.id} className="selfservice-participant-row">
                          <div className="selfservice-participant-info">
                            <div className="selfservice-participant-name">
                              {addon.isAccommodation && <Hotel size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                              {addon.name}
                              {(addon.isIncludedInTourPrice || addon.quantityFree > 0) && (
                                <span className="selfservice-addon-free-badge">
                                  {isFullyInclusive
                                    ? 'Inklusive'
                                    : `${getAdjustedQuantityFree(addon)} inkl.`}
                                </span>
                              )}
                            </div>
                            <div className="selfservice-participant-detail">
                              {isFullyInclusive
                                ? 'Im Tourpreis enthalten'
                                : `${formatCurrency(addon.unitPrice)} pro Einheit`}
                              {nurAnzeige && !isFullyInclusive && ' (Änderung über Frankenfloß)'}
                            </div>
                          </div>
                          <div className="selfservice-addon-total-col">
                            <span className={`selfservice-addon-line-total${isChanged ? ' changed' : ''}`}>
                              {isFullyInclusive
                                ? 'Inklusive'
                                : formatCurrency(paidCost)}
                            </span>
                          </div>
                          {isFullyInclusive || nurAnzeige ? (
                            <div className="selfservice-counter">
                              <span className="selfservice-counter-value">{effectiveQty}</span>
                            </div>
                          ) : (
                          <div className="selfservice-counter">
                            <button
                              className="selfservice-counter-btn"
                              onClick={() => setEffectiveAddonQuantity(addon, naechsteMenge(effectiveQty, -1, grenzen))}
                              disabled={!availability.addOnChangesAllowed || effectiveQty <= minQty}
                            ><Minus size={16} /></button>
                            <span className={`selfservice-counter-value${isChanged ? ' changed' : ''}`}>
                              {effectiveQty}
                            </span>
                            <button
                              className="selfservice-counter-btn"
                              onClick={() => setEffectiveAddonQuantity(addon, naechsteMenge(effectiveQty, 1, grenzen))}
                              disabled={!availability.addOnChangesAllowed || naechsteMenge(effectiveQty, 1, grenzen) === effectiveQty}
                            ><Plus size={16} /></button>
                          </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* === SECTION: Weitere Extras hinzufügen === */}
              {availability.addOnChangesAllowed && weitereAddOns.length > 0 && (
                <div className="selfservice-section">
                  <div className="selfservice-section-header">
                    <Plus size={18} />
                    <h4>Weitere Extras hinzufügen</h4>
                  </div>
                  <div className="selfservice-section-body">
                    {weitereAddOns.map((addon) => {
                      const qty = newAddons[addon.addOnServiceId] ?? 0;
                      // T-095 (v1.8.55, Review): Mindest- und Höchstmenge je Buchung
                      const grenzenNeu = {
                        min: 0,
                        mindestMenge: artVon(addon) === 'Zusatz' ? addon.mindestMenge : 0,
                        hoechstMenge: addon.hoechstMenge
                      };
                      return (
                        <div key={addon.addOnServiceId} className="selfservice-participant-row">
                          <div className="selfservice-participant-info">
                            <div className="selfservice-participant-name">
                              {addon.isAccommodation && <Hotel size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                              {addon.name}
                              {addon.isIncludedInTourPrice && (
                                <span className="selfservice-addon-free-badge">im Tourpreis</span>
                              )}
                            </div>
                            <div className="selfservice-participant-detail">
                              {addon.unitPrice > 0 ? `${formatCurrency(addon.unitPrice)} pro Einheit` : 'Kostenlos'}
                            </div>
                          </div>
                          {qty > 0 && (
                            <div className="selfservice-addon-total-col">
                              <span className="selfservice-addon-line-total changed">
                                {formatCurrency(addon.unitPrice * bezahlteMenge(artVon(addon), addon.pricingType, qty, berechtigteNeu, 0))}
                              </span>
                            </div>
                          )}
                          <div className="selfservice-counter">
                            <button
                              className="selfservice-counter-btn"
                              onClick={() => setNewAddons(prev => ({
                                ...prev,
                                [addon.addOnServiceId]: naechsteMenge(qty, -1, grenzenNeu)
                              }))}
                              disabled={qty <= 0}
                            ><Minus size={16} /></button>
                            <span className={`selfservice-counter-value${qty > 0 ? ' changed' : ''}`}>
                              {qty}
                            </span>
                            <button
                              className="selfservice-counter-btn"
                              onClick={() => setNewAddons(prev => ({
                                ...prev,
                                [addon.addOnServiceId]: naechsteMenge(qty, 1, grenzenNeu)
                              }))}
                              disabled={naechsteMenge(qty, 1, grenzenNeu) === qty}
                            ><Plus size={16} /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* === SECTION: Gutschein === */}
              <div className="selfservice-section">
                <div className="selfservice-section-header">
                  <Gift size={18} />
                  <h4>Gutschein</h4>
                </div>
                <div className="selfservice-section-body">
                  {/* Existing voucher */}
                  {existingVoucherDiscount > 0 && (
                    <div className="selfservice-voucher-row">
                      <span className="selfservice-voucher-label">
                        <Tag size={16} />
                        {availability?.voucherCode || booking.voucherCode || 'Gutschein'}
                      </span>
                      <span className="selfservice-voucher-amount">-{formatCurrency(existingVoucherDiscount)}</span>
                    </div>
                  )}

                  {/* New voucher input */}
                  {!appliedVoucher ? (
                    <div className="selfservice-voucher-form">
                      <div className="selfservice-voucher-input-row">
                        <input
                          type="text"
                          value={voucherInput}
                          onChange={(e) => {
                            setVoucherInput(e.target.value.toUpperCase());
                            setVoucherError(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleVoucherValidate();
                            }
                          }}
                          placeholder="GS-2025-XXXX"
                          disabled={voucherValidating}
                          className={`selfservice-voucher-input${voucherError ? ' error' : ''}`}
                          maxLength={20}
                        />
                        <button
                          type="button"
                          onClick={handleVoucherValidate}
                          disabled={voucherValidating || !voucherInput.trim()}
                          className="selfservice-voucher-validate-btn"
                        >
                          {voucherValidating ? 'Prüfe...' : 'Einlösen'}
                        </button>
                      </div>
                      {voucherError && (
                        <div className="selfservice-voucher-error-msg">
                          <AlertTriangle size={14} />
                          <span>{voucherError}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="selfservice-voucher-applied">
                      <div className="selfservice-voucher-applied-header">
                        <CheckCircle size={16} style={{ color: '#16a34a' }} />
                        <span>Gutschein angewendet</span>
                      </div>
                      <div className="selfservice-voucher-applied-details">
                        <div className="selfservice-voucher-detail-row">
                          <span>Code:</span>
                          <strong>{appliedVoucher.code}</strong>
                        </div>
                        <div className="selfservice-voucher-detail-row">
                          <span>Gutscheinwert:</span>
                          <span>{formatCurrency(appliedVoucher.remainingAmount)}</span>
                        </div>
                        <div className="selfservice-voucher-detail-row highlight">
                          <span>Abzug:</span>
                          <span className="selfservice-voucher-amount">
                            -{formatCurrency(Math.min(appliedVoucher.remainingAmount, Math.max(0, estimatedDifference + newVoucherDiscount)))}
                          </span>
                        </div>
                        {/* T-035: keine Teileinlösung, Restwert verfällt */}
                        {Math.min(appliedVoucher.remainingAmount, Math.max(0, estimatedDifference + newVoucherDiscount)) < appliedVoucher.remainingAmount && (
                          <div className="selfservice-voucher-detail-row">
                            <span style={{ fontSize: '0.85em', color: '#92400e' }}>
                              Hinweis: Der Gutschein wird vollständig eingelöst, ein nicht verbrauchter Restwert verfällt.
                            </span>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleVoucherRemove}
                        className="selfservice-voucher-remove-btn"
                      >
                        Gutschein entfernen
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* === SECTION: Preisübersicht === */}
              <div className="selfservice-price-summary">
                <div className="selfservice-section-header">
                  <Euro size={18} />
                  <h4>Preisübersicht</h4>
                </div>

                {/* Base price */}
                <div className="selfservice-price-row">
                  <span>Erwachsene ({newAdultCount} × {formatCurrency(basePrice)})</span>
                  <span className={`selfservice-price-amount${newAdultCount !== originalAdultCount ? ' changed' : ''}`}>
                    {formatCurrency(newAdultPrice)}
                  </span>
                </div>

                {(booking.participants.childCount > 0 || booking.participants.infantCount > 0) && (
                  <div className="selfservice-price-row sub" style={{ color: '#6b7280' }}>
                    <span>
                      Kinder gemäß Buchung (Änderung nur über Frankenfloß)
                    </span>
                    <span style={{ fontStyle: 'italic' }}>siehe Rechnung</span>
                  </div>
                )}

                {/* Addons from server - only show paid costs */}
                {currentAddOns.map((addon) => {
                  const effectiveQty = getEffectiveAddonQuantity(addon);
                  const isChanged = effectiveQty !== addon.quantity;
                  const paidCost = getAddonPaidCost(addon, effectiveQty);
                  const art = artVon(addon);
                  // T-095 (v1.8.55): freie Menge wie die Rechnung
                  const adjustedFreeQty = (art === 'Wahlgericht' || art === 'Inklusiv')
                    ? effectiveQty - getAddonPaidQty(addon, effectiveQty)
                    : getAdjustedQuantityFree(addon);
                  const paidQty = getAddonPaidQty(addon, effectiveQty);

                  if (effectiveQty === 0 && addon.quantity === 0) return null;

                  if ((art === 'Wahlgericht' || art === 'Inklusiv') && paidQty === 0) {
                    // Fully inclusive - show as "Inklusive" with 0 cost
                    return (
                      <div key={addon.id} className="selfservice-price-row sub" style={{ color: '#6b7280' }}>
                        <span>{addon.name} ({effectiveQty}× inklusive)</span>
                        <span style={{ fontStyle: 'italic' }}>Inklusive</span>
                      </div>
                    );
                  }

                  return (
                    <div key={addon.id} className="selfservice-price-row sub">
                      <span>
                        {addon.name}
                        {paidQty > 0
                          ? ` (${paidQty}× kostenpflichtig${adjustedFreeQty > 0 ? `, ${Math.min(effectiveQty, adjustedFreeQty)} inkl.` : ''})`
                          : ` (${effectiveQty}× inkl.)`}
                      </span>
                      <span className={isChanged ? 'selfservice-price-amount changed' : ''}>
                        {formatCurrency(paidCost)}
                      </span>
                    </div>
                  );
                })}

                {/* New addons */}
                {Object.entries(newAddons).map(([idStr, qty]) => {
                  if (qty <= 0) return null;
                  const addon = availableNewAddOns.find(a => a.addOnServiceId === parseInt(idStr));
                  if (!addon) return null;
                  // T-095 (v1.8.55): neue Gerichte (Auswahl pro Teilnehmer) und Inklusivleistungen frei bis zur Zahl der Berechtigten
                  const bezahltNeu = bezahlteMenge(artVon(addon), addon.pricingType, qty, berechtigteNeu, 0);
                  return (
                    <div key={idStr} className="selfservice-price-row sub">
                      <span>{addon.name} ({qty}×{bezahltNeu < qty ? `, ${qty - bezahltNeu} inkl.` : ''}) <span style={{ color: '#910F0F', fontSize: 11 }}>NEU</span></span>
                      <span className="selfservice-price-amount changed">{formatCurrency(addon.unitPrice * bezahltNeu)}</span>
                    </div>
                  );
                })}

                {/* Existing Voucher */}
                {existingVoucherDiscount > 0 && (
                  <div className="selfservice-price-row" style={{ color: '#16a34a' }}>
                    <span>Gutschein (bereits eingelöst)</span>
                    <span style={{ fontWeight: 600 }}>-{formatCurrency(existingVoucherDiscount)}</span>
                  </div>
                )}

                {/* New Voucher */}
                {appliedVoucher && (
                  <div className="selfservice-price-row" style={{ color: '#16a34a' }}>
                    <span>Gutschein {appliedVoucher.code}</span>
                    <span style={{ fontWeight: 600 }}>
                      -{formatCurrency(Math.min(appliedVoucher.remainingAmount, Math.max(0, estimatedDifference + newVoucherDiscount)))}
                    </span>
                  </div>
                )}

                <div className="selfservice-price-divider" />

                {/* Total */}
                <div className="selfservice-price-row total">
                  <span>{hasChanges() ? 'Neuer Gesamtpreis' : 'Gesamtpreis'}</span>
                  <span className={hasChanges() ? 'selfservice-price-amount changed' : ''}>
                    {formatCurrency(hasChanges() ? estimatedNewTotal : booking.totalPrice)}
                  </span>
                </div>

                {/* Nachzahlung */}
                {hasChanges() && estimatedDifference > 0 && (
                  <div className="selfservice-price-row nachzahlung">
                    <span>Nachzahlung (geschätzt)</span>
                    <span>+{formatCurrency(Math.max(0, estimatedDifference - newVoucherDiscount))}</span>
                  </div>
                )}

                <div style={{ padding: '4px 16px 8px', fontSize: '11px', color: '#6b7280', textAlign: 'right' }}>
                  inkl. MwSt.
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="selfservice-error">
                  <AlertTriangle />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && availability?.isAvailable && (
          <div className="selfservice-footer">
            <button className="selfservice-btn secondary" onClick={onClose}>
              Abbrechen
            </button>
            <button
              className="selfservice-btn primary"
              onClick={validateAndPreview}
              disabled={!hasChanges() || validating}
            >
              {validating ? <><span className="selfservice-btn-spinner" /> Prüfen...</> : 'Weiter zur Vorschau'}
            </button>
          </div>
        )}

        {!loading && availability && !availability.isAvailable && (
          <div className="selfservice-footer">
            <button className="selfservice-btn secondary" onClick={onClose}>
              Schließen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfServiceBookingDialog;
